/**
 * "Triangle of safety" for cascading menus, read from pointer direction rather
 * than the literal triangle — the submenu is portalled and caller-supplied.
 */

export type SubmenuSide = "left" | "right";

export interface PointerPosition {
  x: number;
  y: number;
}

/** Hover delay before a sibling row opens its own submenu. */
export const SUBMENU_OPEN_DELAY_MS = 150;

/** Hover delay while the pointer is in transit toward an open submenu. */
export const SUBMENU_OPEN_DELAY_IN_TRANSIT_MS = 500;

/** How long a move toward the submenu keeps protecting it from sibling hovers. */
export const SUBMENU_HOVER_INTENT_WINDOW_MS = 400;

/** Ignore sub-pixel jitter, which carries no directional information. */
const MIN_TRAVEL_TOWARD_SUBMENU_PX = 2;

/**
 * Share of vertical travel the horizontal travel must match to count as transit.
 * Below 1 so a steep diagonal to a tall submenu's last row still counts.
 */
const HORIZONTAL_DOMINANCE_RATIO = 0.3;

/**
 * Whether the move from `previous` to `current` is heading for a submenu opened
 * on `side` of the menu.
 */
export function isHeadingTowardSubmenu(
  previous: PointerPosition,
  current: PointerPosition,
  side: SubmenuSide,
): boolean {
  const dx = current.x - previous.x;
  const towardSubmenu = side === "right" ? dx : -dx;
  if (towardSubmenu < MIN_TRAVEL_TOWARD_SUBMENU_PX) {
    return false;
  }
  const dy = Math.abs(current.y - previous.y);
  return towardSubmenu >= dy * HORIZONTAL_DOMINANCE_RATIO;
}

/**
 * @param lastHeadingTowardSubmenuMs When the pointer was last heading for the
 *   open submenu, or `null` if it hasn't been (or no submenu is open).
 */
export function submenuOpenDelayMs({
  lastHeadingTowardSubmenuMs,
  nowMs,
}: {
  lastHeadingTowardSubmenuMs: number | null;
  nowMs: number;
}): number {
  if (lastHeadingTowardSubmenuMs === null) {
    return SUBMENU_OPEN_DELAY_MS;
  }
  const elapsed = nowMs - lastHeadingTowardSubmenuMs;
  return elapsed >= 0 && elapsed < SUBMENU_HOVER_INTENT_WINDOW_MS
    ? SUBMENU_OPEN_DELAY_IN_TRANSIT_MS
    : SUBMENU_OPEN_DELAY_MS;
}

/** Which side of the menu a submenu with `placement` opens on. */
export function submenuSideForPlacement(
  placement: string | undefined,
): SubmenuSide {
  return placement?.startsWith("left") ? "left" : "right";
}

/**
 * Pointer state shared by every row of one menu. Held in a ref and mutated in
 * place — it is read inside hover timers and must never trigger a re-render.
 */
export interface SubmenuHoverIntent {
  pointer: PointerPosition | null;
  lastHeadingTowardSubmenuMs: number | null;
}

export function createSubmenuHoverIntent(): SubmenuHoverIntent {
  return { pointer: null, lastHeadingTowardSubmenuMs: null };
}

/**
 * Fold a pointer sample into `intent`. A null `submenuSide` (nothing open)
 * clears the flag so stale transit can't hold a later hover hostage.
 */
export function trackSubmenuHoverIntent(
  intent: SubmenuHoverIntent,
  position: PointerPosition,
  submenuSide: SubmenuSide | null,
  nowMs: number,
): void {
  const previous = intent.pointer;
  intent.pointer = position;
  if (submenuSide === null) {
    intent.lastHeadingTowardSubmenuMs = null;
    return;
  }
  if (previous && isHeadingTowardSubmenu(previous, position, submenuSide)) {
    intent.lastHeadingTowardSubmenuMs = nowMs;
  }
}
